import { GameState, InputState, BuildingType, ItemType } from './types';
import { BUILDING_DEFS } from './data/buildings';
import { RECIPES } from './data/recipes';
import { canCraft, craftItem } from './systems/crafting';
import { tryPlaceBuilding } from './systems/buildMode';
import { tryUpgradeBase, getNextLevelCost, getBaseLevelName } from './systems/progression';
import { saveGame, clearSave } from './save';

export function updateHUD(state: GameState): void {
  const woodEl = document.getElementById('hud-wood');
  const coinsEl = document.getElementById('hud-coins');
  const spearsEl = document.getElementById('hud-spears');
  const baseEl = document.getElementById('hud-base');

  if (woodEl) woodEl.textContent = `Wood: ${state.player.wood}`;
  if (coinsEl) coinsEl.textContent = `Coins: ${state.player.coins}`;
  if (spearsEl) spearsEl.textContent = `Spears: ${state.player.spears}`;
  if (baseEl) baseEl.textContent = `Base Lv: ${state.baseLevel}`;
}

let onNewGame: (() => void) | null = null;

export function setOnNewGame(cb: () => void): void {
  onNewGame = cb;
}

export function initUI(state: GameState): void {
  initVendorModal(state);
  initCraftingModal(state);
  initBuildPanel(state);
  initPauseModal(state);
  initWinModal();
  initUpgradeModal(state);
  initHudPauseButton(state);
  initConfirmNewGameModal();
}

function initVendorModal(state: GameState): void {
  const sellBtn = document.getElementById('vendor-sell');
  const closeBtn = document.getElementById('vendor-close');
  const modal = document.getElementById('vendor-modal');

  if (sellBtn) {
    sellBtn.addEventListener('click', () => {
      // Selling is handled in player.ts via interact button
      if (modal) modal.classList.add('hidden');
    });
  }
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      if (modal) modal.classList.add('hidden');
    });
  }
}

function initCraftingModal(state: GameState): void {
  const closeBtn = document.getElementById('crafting-close');
  const modal = document.getElementById('crafting-modal');

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      if (modal) modal.classList.add('hidden');
    });
  }
}

export function showCraftingModal(state: GameState): void {
  const modal = document.getElementById('crafting-modal');
  const list = document.getElementById('crafting-list');
  if (!modal || !list) return;

  list.innerHTML = '';
  for (const recipe of RECIPES) {
    const item = document.createElement('div');
    item.className = 'recipe-item';

    const info = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'recipe-name';
    name.textContent = recipe.name;
    const cost = document.createElement('div');
    cost.className = 'recipe-cost';
    cost.textContent = `${recipe.woodCost} wood, ${recipe.coinCost} coins`;
    const desc = document.createElement('div');
    desc.style.fontSize = '12px';
    desc.style.color = '#7a5a3a';
    desc.textContent = recipe.description;
    info.appendChild(name);
    info.appendChild(cost);
    info.appendChild(desc);

    const btn = document.createElement('button');
    btn.textContent = 'Craft';
    btn.disabled = !canCraft(state, recipe.id);

    if (recipe.id === ItemType.SuperAxe && state.player.hasSuperAxe) {
      btn.textContent = 'Owned';
      btn.disabled = true;
    }

    btn.addEventListener('click', () => {
      if (craftItem(state, recipe.id)) {
        showCraftingModal(state); // Refresh
        updateHUD(state);
      }
    });

    item.appendChild(info);
    item.appendChild(btn);
    list.appendChild(item);
  }

  modal.classList.remove('hidden');
}

function initBuildPanel(state: GameState): void {
  const cancelBtn = document.getElementById('build-cancel');
  const modal = document.getElementById('build-panel');

  if (cancelBtn) {
    cancelBtn.addEventListener('click', () => {
      state.buildMode = false;
      state.buildSelection = null;
      if (modal) modal.classList.add('hidden');
    });
  }
}

export function showBuildPanel(state: GameState): void {
  const modal = document.getElementById('build-panel');
  const list = document.getElementById('build-list');
  if (!modal || !list) return;

  list.innerHTML = '';
  for (const def of BUILDING_DEFS) {
    const item = document.createElement('div');
    item.className = 'build-item';

    const info = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'build-name';
    name.textContent = def.name;
    const cost = document.createElement('div');
    cost.className = 'build-cost';
    let costText = `${def.woodCost} wood`;
    if (def.coinCost > 0) costText += `, ${def.coinCost} coins`;
    cost.textContent = costText;
    info.appendChild(name);
    info.appendChild(cost);

    const btn = document.createElement('button');
    btn.textContent = 'Select';
    const canAfford = state.player.wood >= def.woodCost && state.player.coins >= def.coinCost;
    btn.disabled = !canAfford;

    btn.addEventListener('click', () => {
      state.buildSelection = def.type;
      if (modal) modal.classList.add('hidden');
    });

    item.appendChild(info);
    item.appendChild(btn);
    list.appendChild(item);
  }

  modal.classList.remove('hidden');
}

function initPauseModal(state: GameState): void {
  const resumeBtn = document.getElementById('pause-resume');
  const saveBtn = document.getElementById('pause-save');
  const newGameBtn = document.getElementById('pause-newgame');
  const modal = document.getElementById('pause-modal');

  if (resumeBtn) {
    resumeBtn.addEventListener('click', () => {
      state.paused = false;
      if (modal) modal.classList.add('hidden');
    });
  }

  if (saveBtn) {
    saveBtn.addEventListener('click', () => {
      saveGame(state);
      if (saveBtn) saveBtn.textContent = 'Saved!';
      setTimeout(() => { if (saveBtn) saveBtn.textContent = 'Save Game'; }, 1500);
    });
  }

  if (newGameBtn) {
    newGameBtn.addEventListener('click', () => {
      if (modal) modal.classList.add('hidden');
      const confirmModal = document.getElementById('confirm-newgame-modal');
      if (confirmModal) confirmModal.classList.remove('hidden');
    });
  }
}

function initHudPauseButton(state: GameState): void {
  const btn = document.getElementById('hud-pause');
  if (btn) {
    btn.addEventListener('click', () => {
      if (!isAnyModalOpen()) {
        state.paused = true;
        const modal = document.getElementById('pause-modal');
        if (modal) modal.classList.remove('hidden');
      }
    });
  }
}

function initConfirmNewGameModal(): void {
  const yesBtn = document.getElementById('confirm-newgame-yes');
  const noBtn = document.getElementById('confirm-newgame-no');
  const modal = document.getElementById('confirm-newgame-modal');

  if (yesBtn) {
    yesBtn.addEventListener('click', () => {
      clearSave();
      if (modal) modal.classList.add('hidden');
      if (onNewGame) onNewGame();
    });
  }

  if (noBtn) {
    noBtn.addEventListener('click', () => {
      if (modal) modal.classList.add('hidden');
      // Re-show pause modal
      const pauseModal = document.getElementById('pause-modal');
      if (pauseModal) pauseModal.classList.remove('hidden');
    });
  }
}

function initWinModal(): void {
  const continueBtn = document.getElementById('win-continue');
  const modal = document.getElementById('win-modal');

  if (continueBtn) {
    continueBtn.addEventListener('click', () => {
      if (modal) modal.classList.add('hidden');
    });
  }
}

function initUpgradeModal(state: GameState): void {
  const buyBtn = document.getElementById('upgrade-buy');
  const closeBtn = document.getElementById('upgrade-close');
  const modal = document.getElementById('upgrade-modal');

  if (buyBtn) {
    buyBtn.addEventListener('click', () => {
      if (tryUpgradeBase(state)) {
        updateUpgradeInfo(state);
        updateHUD(state);
      }
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      if (modal) modal.classList.add('hidden');
    });
  }
}

export function updateUpgradeInfo(state: GameState): void {
  const info = document.getElementById('upgrade-info');
  const buyBtn = document.getElementById('upgrade-buy') as HTMLButtonElement | null;
  if (!info) return;

  const cost = getNextLevelCost(state);
  const currentName = getBaseLevelName(state.baseLevel);

  if (cost < 0) {
    info.textContent = `Your base "${currentName}" is fully upgraded!`;
    if (buyBtn) buyBtn.disabled = true;
  } else {
    const nextName = getBaseLevelName(state.baseLevel + 1);
    info.textContent = `Current: "${currentName}" (Lv ${state.baseLevel})\nUpgrade to "${nextName}" for ${cost} coins?`;
    if (buyBtn) buyBtn.disabled = state.player.coins < cost;
  }
}

export function handleUIInput(state: GameState, input: InputState): void {
  // Check if any modal is open
  const anyModalOpen = isAnyModalOpen();

  if (input.pause) {
    if (anyModalOpen) {
      closeAllModals();
      state.paused = false;
      state.buildMode = false;
      state.buildSelection = null;
    } else {
      state.paused = true;
      const modal = document.getElementById('pause-modal');
      if (modal) modal.classList.remove('hidden');
    }
    return;
  }

  if (anyModalOpen) return; // Don't process game inputs while modal is open

  if (input.craft) {
    showCraftingModal(state);
    return;
  }

  if (input.buildToggle) {
    if (state.buildMode) {
      state.buildMode = false;
      state.buildSelection = null;
    } else {
      state.buildMode = true;
      showBuildPanel(state);
    }
    return;
  }

  // In build mode, confirm places building
  if (state.buildMode && state.buildSelection && input.chop) {
    if (tryPlaceBuilding(state)) {
      updateHUD(state);
    }
  }
}

export function isAnyModalOpen(): boolean {
  const modals = ['vendor-modal', 'crafting-modal', 'build-panel', 'pause-modal', 'win-modal', 'upgrade-modal', 'tutorial', 'confirm-newgame-modal'];
  return modals.some(id => {
    const el = document.getElementById(id);
    return el && !el.classList.contains('hidden');
  });
}

function closeAllModals(): void {
  const modals = ['vendor-modal', 'crafting-modal', 'build-panel', 'pause-modal', 'win-modal', 'upgrade-modal', 'confirm-newgame-modal'];
  for (const id of modals) {
    const el = document.getElementById(id);
    if (el) el.classList.add('hidden');
  }
}
